import { localDateSort } from 'components/Tables/dateSorting';
import formatJson from 'utils/formatJson';
import React, { useMemo, useState } from 'react';
import { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { Modal, Table, Button } from 'antd';

import { FIELDS_NAMES } from './const';
import { TableScroll } from './types';
import numeral from 'numeral';
import { CURRENCY_FORMAT } from '../../constants/numberformats';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);

interface IFeeTransactionsTableProps {
  data: any;
  scroll?: TableScroll;
}
const FeeTransactionsTable = ({ data, scroll }: IFeeTransactionsTableProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');

  const showModal = (response: string) => {
    setIsModalOpen(true);
    setCurrentResponse(formatJson(response) ?? response ?? '');
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setCurrentResponse('');
  };

  const handleOk = () => {
    setIsModalOpen(false);
    setCurrentResponse('');
  };

  const columns: ColumnType<any>[] = useMemo(() => {
    return [
      {
        title: FIELDS_NAMES.DATE,
        key: 'date',
        dataIndex: 'date',
        ...localDateSort('date', true),

        render: (data: string) =>
          data ? dayjs(data).format('LLL') : '- not set -',
      },
      {
        title: FIELDS_NAMES.TOTAL_AMOUNT,
        key: 'total amount',
        dataIndex: 'amount',
        sorter: (recordA: { amount: number }, recordB: { amount: number }) =>
          recordA.amount - recordB.amount,
        render: (amount: number) => numeral(amount).format(CURRENCY_FORMAT),
      },
      {
        title: FIELDS_NAMES.AMOUNT_CHARGED,
        key: 'amount charged',
        dataIndex: 'is_successful',
        sorter: (
          recordA: { amount: number; is_successful: boolean },
          recordB: { amount: number; is_successful: boolean },
        ) =>
          (recordA.is_successful ? recordA.amount : 0) -
          (recordB.is_successful ? recordB.amount : 0),
        render: (is_successful: boolean, record: { amount: number }) =>
          numeral(is_successful ? record.amount : 0).format(CURRENCY_FORMAT),
      },
      {
        title: FIELDS_NAMES.SUCCESS,
        key: 'success',
        dataIndex: 'is_successful',
        sorter: (recordA: any, recordB: any) =>
          (recordA.is_successful ? 'Yes' : 'No').localeCompare(
            recordB.is_successful ? 'Yes' : 'No',
          ),
        render: (is_successful: boolean) => (is_successful ? 'Yes' : 'No'),
      },
      {
        title: FIELDS_NAMES.RESPONSE,
        key: 'response',
        dataIndex: 'response',
        width: 300,
        render: (response: string) => (
          <div>
            <span>{response.slice(0, 20)}...</span>
            <Button type="link" onClick={() => showModal(response)}>
              View Details
            </Button>
          </div>
        ),
      },
    ];
  }, []);

  return (
    <>
      <Modal
        title="Response modal"
        open={isModalOpen}
        width={800}
        style={{ maxWidth: 'calc(100vw - 48px)' }}
        styles={{
          body: {
            maxHeight: '70vh',
            overflow: 'auto',
          },
        }}
        footer={[
          <Button key="submit" type="primary" onClick={handleOk}>
            OK
          </Button>,
        ]}
        onCancel={handleCancel}
      >
        <pre
          style={{
            margin: 0,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          {currentResponse}
        </pre>
      </Modal>
      <Table
        tableLayout="fixed"
        rowKey={'record_id'}
        dataSource={data}
        columns={columns}
        pagination={false}
        scroll={scroll}
      />
    </>
  );
};

export default FeeTransactionsTable;
